import { useEffect } from "react";
import { BackButton, Button } from "../../components";
import { CancellationServiceSteps } from "./types";
import { useNavigate, useSearchParams } from "react-router-dom";
import { checkAllKeysHaveValue, classNames, navigateTo } from "../../helper";
import { PiCheckBold } from "react-icons/pi";
import RouteNames from "../../routes/type";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import CustomerDetails from "./CustomerDetails";
import PurchaseDetails from "./PurchaseDetails";
import queryString from "query-string";
import SubmitCancellationService from "./SubmitCancellationService";
import { connect, useDispatch } from "react-redux";
import CancellationReason from "./CancellationReason";
import {
  createCancellationTicket,
  updateCancellationState,
} from "../../store/cancellation-service/actions";
import ReviewCancellationService from "./ReviewCancellationService";
import { RootState } from "../../store/root-reducer";
import Loader from "../../components/Loader";
import { isScopeLoading } from "../../store/scope-loading/helpers";
import { CancellationServiceScope } from "../../store/cancellation-service/types";
import toast from "react-hot-toast";
import { UserScope } from "../../store/user/types";

function CancellationService({ data, loading }) {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const formMethods = useForm({
    defaultValues: {
      purchaseDetails: data.purchaseDetails,
      customerDetails: data.customerDetails,
      cancellation_reason: data.cancellation_reason,
    },
  });
  const { handleSubmit, getValues } = formMethods;
  const { t } = useTranslation();
  const stepId = parseInt(searchParams.get("step") || "");
  const tempValidationId = searchParams.get("id") || "";

  const currentStep = CancellationServiceSteps.find(
    (item) => item.step_num === stepId
  );

  useEffect(() => {
    if (
      (!stepId && !/^[0-9]*$/.test(`${stepId}`)) ||
      stepId > 5 ||
      stepId < 1 ||
      !tempValidationId
    ) {
      navigate(`/${RouteNames.CANCELLATION_SERVICE}?step=1`, { replace: true });
    }
    return () => {};
  }, [navigate, stepId, tempValidationId]);

  const navigateToNextStep = (step, otherPrams = {}) => {
    const parsed = queryString.parse(window.location.search);
    window.scrollTo(0, 0);
    navigateTo(
      `/${RouteNames.CANCELLATION_SERVICE}?${queryString.stringify({
        ...parsed,
        ...otherPrams,
        step: step ?? stepId + 1,
      })}`
    );
  };

  const handleNext = () => {
    switch (stepId) {
      case 1:
        const customerDetails = getValues("customerDetails");
        dispatch(
          updateCancellationState({
            customerDetails: {
              name: customerDetails?.name,
              email: customerDetails?.email,
              phone: customerDetails?.phone,
              address: customerDetails?.address,
              city: customerDetails?.city,
              state: customerDetails?.state,
              zip_code: customerDetails?.zip_code,
            },
          })
        );
        navigateToNextStep(stepId + 1, {
          id: new Date().getTime(),
        });
        break;

      case 2:
        const purchaseDetails = getValues("purchaseDetails");
        dispatch(
          updateCancellationState({
            purchaseDetails: {
              invoice: purchaseDetails?.invoice,
              date_of_purchase: purchaseDetails?.date_of_purchase,
              store_name: purchaseDetails?.store_name,
              store_phone: purchaseDetails?.store_phone,
              store_address: purchaseDetails?.store_address,
              store_city: purchaseDetails?.store_city,
              store_state: purchaseDetails?.store_state,
              were_any_items_exchanged:
                purchaseDetails?.were_any_items_exchanged,
              store_zip: purchaseDetails?.store_zip,
            },
          })
        );
        navigateToNextStep(stepId + 1);
        break;

      case 3:
        const cancellation_reason = getValues("cancellation_reason");
        dispatch(
          updateCancellationState({
            cancellation_reason,
          })
        );
        navigateToNextStep(stepId + 1);
        break;

      case 4:
        if (checkAllKeysHaveValue(data)) {
          dispatch(createCancellationTicket());
        } else {
          toast.error(
            "Please complete all the missing data and try submitting again.",
            {
              position: "bottom-center",
              id: "form_invalid",
            }
          );
        }
        break;

      default:
        break;
    }
  };

  const renderStepComponent = () => {
    switch (stepId) {
      case 1:
        return <CustomerDetails key="stepId1" />;
      case 2:
        return <PurchaseDetails key="stepId2" />;
      case 3:
        return <CancellationReason key="stepId3" />;
      case 4:
        return <ReviewCancellationService key="stepId4" />;
      default:
        return null;
    }
  };

  if (stepId === 5) {
    return <SubmitCancellationService />;
  }

  return (
    <div className="px-4 bg-white">
      <div className="mb-8 mt-6 flex justify-between items-center">
        <BackButton
          onClick={() => {
            if (stepId === 1) {
              navigate("/");
            } else {
              navigateToNextStep(stepId - 1);
            }
          }}
          text={stepId === 1 ? t("Home") : t("Back")}
        />
        <div className="flex flex-row gap-2">
          {CancellationServiceSteps.map(({ step_num: step }) => {
            const isCompleted = step < stepId;
            const isDraft = step > stepId;
            return (
              <button
                disabled
                key={step}
                className={classNames(
                  "size-7 flex rounded-full justify-center items-center text-base",
                  isDraft
                    ? "bg-[#009CAA] bg-opacity-15 text-[#009CAA]"
                    : "bg-[#009CAA] text-white"
                )}
              >
                {isCompleted ? <PiCheckBold size={16} color="white" /> : step}
              </button>
            );
          })}
        </div>
      </div>
      <div className="mb-2">
        <h3 className="text-2xl font-semibold">{t(currentStep?.title)}</h3>
      </div>
      <form className="grid gap-y-4" onSubmit={handleSubmit(handleNext)}>
        <Loader isLoading={loading} />
        <FormProvider {...formMethods}>{renderStepComponent()}</FormProvider>
        <Button submit className="my-4">
          {stepId === 4 ? t("Submit") : t("Next")}
        </Button>
      </form>
    </div>
  );
}

const mapStateToProps = (state: RootState) => ({
  data: state?.cancellationService || {},
  loading:
    isScopeLoading(state.scopeLoading, CancellationServiceScope) ||
    isScopeLoading(state.scopeLoading, UserScope),
});

export default connect(mapStateToProps)(CancellationService);
